import axiosInstance from "../utils/axiosInstance";

/**
 * PUBLIC — ask the backend whether this build needs an update.
 *
 * `store` and `build` are optional: omitting them is exactly what a pre-bucket
 * client does, and the backend resolves that to the platform's default bucket
 * and the version axis. Sending them lets Android be judged on its versionCode,
 * which is the number Play actually orders releases by.
 *
 * Returns { updateType: "none" | "recommended" | "force", latestVersion,
 * storeUrl, title, message, store, comparedBy, reasons, ... }.
 */
export async function checkAppVersion({ platform, version, build, store } = {}) {
  const { data } = await axiosInstance.get("/app-version/check", {
    // Undefined params are dropped by axios, so an unknown build or store is
    // simply absent rather than sent as the string "undefined".
    params: {
      platform,
      version,
      ...(build != null && build !== "" ? { build } : {}),
      ...(store ? { store } : {}),
    },
  });
  return data?.data ?? { updateType: "none" };
}

/**
 * SUPER-ADMIN — the store buckets (ios/appstore, android/play), including any
 * that has no policy saved yet.
 */
export async function getAppVersionConfig() {
  const { data } = await axiosInstance.get("/app-version/config");
  return data?.data?.buckets ?? [];
}

/**
 * SUPER-ADMIN — partial update of one bucket. Only the keys you pass change.
 *
 * Wire field names are camelCase: latestVersion, recommendedVersion, minVersion,
 * latestBuild, recommendedBuild, minBuild, enforce, isActive, storeUrl, title,
 * message, notes.
 *
 * Resolves to the saved bucket plus a `warnings` array — non-fatal
 * misconfigurations that would save cleanly and then quietly do nothing (a
 * floor above latest, a build floor with no version floor, blocking with no
 * store URL). Surface them; they are the entire troubleshooting section of the
 * release runbook, discovered the hard way.
 */
export async function patchAppVersionBucket(platform, store, payload) {
  const { data } = await axiosInstance.patch(
    `/app-version/config/${platform}/${store}`,
    payload
  );
  return data?.data;
}

/**
 * SUPER-ADMIN — DEPRECATED, kept only so anything still on the old two-platform
 * shape keeps working. New code should use getAppVersionConfig.
 */
export async function getAppVersions() {
  const { data } = await axiosInstance.get("/app-version");
  return data?.data ?? { ios: null, android: null, buckets: [] };
}

/**
 * SUPER-ADMIN — DEPRECATED. Writes only the version axis of the platform's
 * default bucket; it has no vocabulary for stores or build numbers.
 */
export async function upsertAppVersion(payload) {
  const { data } = await axiosInstance.put("/app-version", payload);
  return data?.data;
}
